import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ComponentChanges } from '../../../../../models/component-changes.model';
import { DatePickerInlineBaseComponent } from '../date-picker-inline-base/date-picker-inline-base.component';
import * as moment from 'moment';

/**
 * This interface is used to represent day on calendar view
 */
interface Day {
  /**
   * The day date
   */
  date: moment.Moment;

  /**
   * The flag to indicate is day is past for current month
   */
  past: boolean;

  /**
   * The flag to indicate is day is future for current month
   */
  future: boolean;

  /**
   * The flag to indicate is day is disabled
   */
  disabled: boolean;

  /**
   * The position mask
   */
  position: CalendarPosition;
}

/**
 * The calendar item position
 */
enum CalendarPosition {
  Default = 1,
  Left = 2,
  Middle = 4,
  Right = 8,
}

@Component({
  selector: 'ideom-date-picker-inline-days',
  templateUrl: './date-picker-inline-days.component.html',
  styleUrls: ['./date-picker-inline-days.component.scss'],
})
export class DatePickerInlineDaysComponent extends DatePickerInlineBaseComponent implements OnInit, OnChanges {
  /**
   * The week days for heading
   */
  weekDays: string[] = [];

  /**
   * The days on calendar view
   */
  days: Day[] = [];

  /**
   * The flag indicating that selection is in progress
   */
  isSelecting: boolean;

  /**
   * @ignore
   */
  CalendarPosition = CalendarPosition;

  /**
   * @ignore
   */
  constructor() {
    super();
  }

  /**
   * @ignore
   * @param changes
   */
  ngOnChanges(changes: ComponentChanges<DatePickerInlineDaysComponent>): void {
    if (changes.disabled || changes.current || changes.min || changes.max) {
      this.updateDays();
    }
  }

  /**
   * @ignore
   */
  ngOnInit(): void {
    this.updateWeekDays();
  }

  /**
   * This method is used for checking day position
   * @param day The day
   * @param position The position
   */
  hasPosition = (day: Day, position: CalendarPosition): boolean => (day.position & position) === position;

  /**
   * This method is used to start range or day selection
   * @param day The day
   */
  onDayClick(day: Day): void {
    if (this.disabled || day.disabled) {
      return;
    }

    const emitDates = (start: moment.Moment, end: moment.Moment) => {
      this.startChange.emit(start);
      this.endChange.emit(end);
      this.currentChange.emit(moment(start));
      this.datesChange.emit();
    };

    if (this.isRange) {
      if (this.isSelecting) {
        if (this.start.isSameOrBefore(day.date)) {
          this.isSelecting = false;
          const end = moment(day.date);
          emitDates(this.start, end);
        }
      } else {
        this.isSelecting = true;
        const start = moment(day.date);
        const end = null;
        emitDates(start, end);
      }
    } else {
      const start = moment(day.date);
      const end = moment(start);
      emitDates(start, end);
    }
  }

  /**
   * This method is used for updating list of calendar days
   * @private
   */
  private updateDays(): void {
    const days: Day[] = [];
    const monthStart = moment(this.current).startOf('month');
    const monthEnd = moment(this.current).endOf('month');
    const monthWeekStart = moment(monthStart).startOf('week');
    const monthWeekEnd = moment(monthEnd).endOf('week');

    let date = moment(monthWeekStart);

    while (date.isSameOrBefore(monthWeekEnd, 'day')) {
      days.push({
        date,
        past: date.isBefore(monthStart),
        future: date.isAfter(monthEnd),
        position: CalendarPosition.Default,
        disabled: this.disabled || (this.min?.isAfter(date, 'day') ?? false) || (this.max?.isBefore(date, 'day') ?? false),
      });
      date = moment(date).add(1, 'days');
    }

    this.days = days;
    this.updateDaysPosition();
  }

  /**
   * This method is used for updating week days
   * @private
   */
  private updateWeekDays(): void {
    let weekStart = moment(this.current).startOf('week');
    const weekEnd = moment(weekStart).endOf('week');

    while (weekStart.isSameOrBefore(weekEnd, 'day')) {
      const weekDay = moment(weekStart);
      this.weekDays.push(weekDay.format('dd'));
      weekStart = moment(weekStart).add(1, 'days');
    }
  }

  private updateDaysPosition(): void {
    this.days.forEach((day: Day) => {
      let position = CalendarPosition.Default;
      if (this.start && day.date.isSame(this.start, 'day')) {
        position |= CalendarPosition.Left;
      }
      if (this.end && day.date.isSame(this.end, 'day')) {
        position |= CalendarPosition.Right;
      }
      if (this.start && this.end && day.date.isSameOrAfter(this.start, 'day') && day.date.isSameOrBefore(this.end, 'day')) {
        position |= CalendarPosition.Middle;
      }
      day.position = position;
    });
  }
}
